import { FileModel } from '@/typings/api/files';
import { F } from '@/utils/intl';
import {
  FileOutlined,
  FilePdfOutlined,
  FontSizeOutlined,
} from '@ant-design/icons';
import { Empty, Tooltip, Typography } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { formatFileSize, isPreviewable } from './fileDisplay';

import styles from './FileGrid.less';

interface FileGridProps {
  files: FileModel[];
  loading?: boolean;
  /** Highlighted as chosen — the picker's current value. */
  selectedId?: string | null;
  onSelect?: (file: FileModel) => void;
  /** Row of buttons rendered on the card; the manager passes its actions here. */
  renderActions?: (file: FileModel) => React.ReactNode;
  emptyText?: React.ReactNode;
}

/** Long enough that a fast load never shows a placeholder at all. */
const PLACEHOLDER_DELAY = 250;
/** Once shown, it stays this long, so it can never flash past. */
const PLACEHOLDER_MIN_VISIBLE = 400;

/**
 * Whether to show the loading placeholder for a load that is `pending`.
 *
 * Most of these loads come back in well under a tenth of a second, and a
 * placeholder that appears and vanishes again in that time reads as a glitch
 * rather than as feedback. So it waits before appearing, and once it has
 * appeared it stays put briefly even if the files have meanwhile arrived.
 */
function useDelayedPlaceholder(pending: boolean) {
  const [visible, setVisible] = useState(false);
  const shownAt = useRef(0);

  useEffect(() => {
    if (pending) {
      if (visible) return undefined;

      const timer = window.setTimeout(() => {
        shownAt.current = Date.now();
        setVisible(true);
      }, PLACEHOLDER_DELAY);

      return () => window.clearTimeout(timer);
    }

    if (!visible) return undefined;

    const remaining = PLACEHOLDER_MIN_VISIBLE - (Date.now() - shownAt.current);
    if (remaining <= 0) {
      setVisible(false);
      return undefined;
    }

    const timer = window.setTimeout(() => setVisible(false), remaining);
    return () => window.clearTimeout(timer);
  }, [pending, visible]);

  return visible;
}

/** A card-shaped stand-in, so the grid does not change shape when files land. */
const PlaceholderCard: React.FC = () => (
  <div className={styles.card}>
    <div className={classNames(styles.placeholderPreview, styles.shimmer)} />
    <div className={styles.meta}>
      <span className={classNames(styles.placeholderLine, styles.shimmer)} />
      <span
        className={classNames(
          styles.placeholderLine,
          styles.placeholderLineShort,
          styles.shimmer,
        )}
      />
    </div>
  </div>
);

/** An icon standing in for a file that cannot show itself. */
const KindIcon: React.FC<{ file: FileModel }> = ({ file }) => {
  if (file.kind === 'font') return <FontSizeOutlined />;
  if (file.kind === 'document') return <FilePdfOutlined />;

  return <FileOutlined />;
};

/**
 * The partner's files as a grid of thumbnails — shared by the file manager and
 * by every picker, so that choosing a file looks the same wherever you are
 * asked to.
 *
 * A grid rather than a table because the overwhelming majority of these are
 * images, and an image is recognised by looking at it, not by reading its name.
 */
const FileGrid: React.FC<FileGridProps> = ({
  files,
  loading,
  selectedId,
  onSelect,
  renderActions,
  emptyText,
}) => {
  const empty = files.length === 0;
  const stillLoading = !!loading && empty;
  const showPlaceholder = useDelayedPlaceholder(stillLoading);

  if (showPlaceholder || stillLoading) {
    // Before the delay is up: an empty grid rather than an empty state, since
    // there is nothing to say yet and "no files" would be a lie half the time.
    return (
      <div
        className={classNames(styles.grid, {
          [styles.placeholders]: showPlaceholder,
        })}
        aria-busy={stillLoading}
      >
        {showPlaceholder &&
          Array.from({ length: 8 }).map((_, index) => (
            <PlaceholderCard key={index} />
          ))}
      </div>
    );
  }

  if (empty) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          emptyText ?? (
            <F
              id="components.fileLibrary.empty"
              defaultMessage="No files yet. Upload one to get started."
            />
          )
        }
      />
    );
  }

  return (
    <div className={styles.grid}>
      {files.map((file) => (
        <div
          key={file.id}
          className={classNames(styles.card, {
            [styles.selectable]: !!onSelect,
            [styles.selected]: selectedId === file.id,
          })}
          onClick={onSelect ? () => onSelect(file) : undefined}
          role={onSelect ? 'button' : undefined}
          tabIndex={onSelect ? 0 : undefined}
          onKeyDown={
            onSelect
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(file);
                  }
                }
              : undefined
          }
        >
          <div className={styles.preview}>
            {isPreviewable(file) ? (
              // Loading is left to the browser: a grid of remote thumbnails is
              // exactly what lazy loading is for.
              <img src={file.url} alt={file.name} loading="lazy" />
            ) : (
              <span className={styles.icon}>
                <KindIcon file={file} />
              </span>
            )}
          </div>

          <div className={styles.meta}>
            <Tooltip title={file.name}>
              <Typography.Text ellipsis className={styles.name}>
                {file.name}
              </Typography.Text>
            </Tooltip>
            <span className={styles.size}>{formatFileSize(file.size)}</span>
          </div>

          {renderActions && (
            // Stops a click on an action from also selecting the card.
            <div
              className={styles.actions}
              onClick={(event) => event.stopPropagation()}
            >
              {renderActions(file)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default FileGrid;
